import React from "react";
import { Users, TrendingUp, Truck, Car, Calendar, ArrowRight, Phone, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";

function StatCard({ icon: Icon, label, value, color, onClick }) {
  return (
    <div 
      className={"bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow cursor-pointer " + (onClick ? "hover:border-blue-300" : "")}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500 mb-1">{label}</p>
          <p className={"text-3xl font-bold " + color}>{value}</p>
        </div>
        <div className={"p-3 rounded-lg " + color.replace("text-", "bg-").replace("-600", "-100")}>
          <Icon size={24} className={color} />
        </div>
      </div>
    </div>
  );
}

function Dashboard({ stats, clients, events, onNavigate }) {
  // Get recent clients (last 5)
  var recentClients = clients.slice().sort(function(a, b) {
    return new Date(b.createdAt) - new Date(a.createdAt);
  }).slice(0, 5);

  // Get upcoming events
  var today = new Date().toISOString().split('T')[0];
  var upcomingEvents = events.filter(function(e) {
    return e.date >= today;
  }).sort(function(a, b) {
    return new Date(a.date) - new Date(b.date);
  }).slice(0, 5);

  var statusLabels = {
    lead: "Lead nuevo",
    contact: "En contacto",
    options: "Opciones enviadas",
    reservation: "Reserva",
    importing: "En importación",
    delivered: "Entregado"
  };

  var statusColors = {
    lead: "bg-blue-100 text-blue-700",
    contact: "bg-yellow-100 text-yellow-700",
    options: "bg-purple-100 text-purple-700",
    reservation: "bg-orange-100 text-orange-700",
    importing: "bg-cyan-100 text-cyan-700",
    delivered: "bg-green-100 text-green-700"
  };

  var eventTypeIcons = {
    call: Phone,
    followup: Clock,
    delivery: Truck
  };

  return (
    <div className="h-screen overflow-y-auto p-8 bg-slate-50" data-testid="dashboard">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-800">Panel General</h1>
        <p className="text-slate-500 mt-1">Resumen del estado del negocio</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        <StatCard 
          icon={Users} 
          label="Clientes Activos" 
          value={stats.totalClients} 
          color="text-blue-600"
          onClick={function() { onNavigate("crm"); }}
        />
        <StatCard 
          icon={TrendingUp} 
          label="Operaciones en Curso" 
          value={stats.inProgress} 
          color="text-emerald-600"
          onClick={function() { onNavigate("crm"); }}
        />
        <StatCard 
          icon={Truck} 
          label="En Importación" 
          value={stats.vehiclesImporting} 
          color="text-cyan-600"
          onClick={function() { onNavigate("imports"); }}
        />
        <StatCard 
          icon={Car} 
          label="Stock Disponible" 
          value={stats.vehiclesAvailable} 
          color="text-purple-600"
          onClick={function() { onNavigate("stock"); }}
        />
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-2 gap-6">
        {/* Recent Clients */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-800">Clientes Recientes</h2>
            <Button variant="ghost" size="sm" onClick={function() { onNavigate("crm"); }} className="text-blue-600">
              Ver todos <ArrowRight size={16} className="ml-1" />
            </Button>
          </div>
          <div className="space-y-3">
            {recentClients.map(function(client) {
              return (
                <div key={client.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-800">{client.name}</p>
                    <p className="text-sm text-slate-500">{client.interest}</p>
                  </div>
                  <span className={"text-xs font-medium px-2 py-1 rounded-full " + statusColors[client.status]}>
                    {statusLabels[client.status]}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-800">Próximas Tareas</h2>
            <Button variant="ghost" size="sm" onClick={function() { onNavigate("calendar"); }} className="text-blue-600">
              Ver calendario <ArrowRight size={16} className="ml-1" />
            </Button>
          </div>
          <div className="space-y-3">
            {upcomingEvents.length === 0 ? (
              <p className="text-slate-500 text-center py-4">No hay tareas pendientes</p>
            ) : (
              upcomingEvents.map(function(event) {
                var Icon = eventTypeIcons[event.type] || Calendar;
                var client = clients.find(function(c) { return c.id === event.clientId; });
                return (
                  <div key={event.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Icon size={18} className="text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{event.title}</p>
                      <p className="text-sm text-slate-500">{client ? client.name : ""}</p>
                    </div>
                    <span className="text-sm text-slate-400">
                      {new Date(event.date).toLocaleDateString("es-ES", { day: "numeric", month: "short" })}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Pipeline Summary */}
      <div className="mt-6 bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h2 className="text-lg font-bold text-slate-800 mb-4">Pipeline Comercial</h2>
        <div className="flex items-center gap-2">
          {Object.entries(statusLabels).map(function(entry, idx) {
            var status = entry[0];
            var label = entry[1];
            var count = clients.filter(function(c) { return c.status === status; }).length;
            return (
              <div key={status} className="flex-1 text-center">
                <div className={"mx-auto w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold " + statusColors[status]}>
                  {count}
                </div>
                <p className="text-xs text-slate-500 mt-2">{label}</p>
                {idx < 5 && <div className="hidden md:block absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 text-slate-300">→</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
